import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import SearchIcon from "@mui/icons-material/Search";
import styles from "./styles.module.scss";
import Modal from "../../../components/common/Modal";
import PrimaryButton from "../../../components/common/PrimaryButton";
import Spinner from "../../../components/common/Spinner";
import OrgAvatar from "../OrgAvatar";
import { IOrganizations } from "../../../setup/types/Organisation";

interface IEditIncludedOrgsModalProps {
  open: boolean;
  /** Saved included codes, in their saved order. */
  includedCodes: string[];
  organizations: IOrganizations[];
  organizationsLoadFailed: boolean;
  handleClose: () => void;
  /** Rejects when saving fails; the modal then stays open with an error. */
  handleSave: (added: string[], removed: string[]) => Promise<void>;
}

interface IColumnProps {
  title: string;
  codes: string[];
  emptyLabel: string;
  renderRow: (code: string) => JSX.Element;
}

const Column = ({ title, codes, emptyLabel, renderRow }: IColumnProps) => (
  <div className={styles.column}>
    <div className={styles.columnHeader}>
      <span className={styles.columnTitle}>{title}</span>
      <span className={styles.countBadge}>{codes.length}</span>
    </div>
    <div className={styles.list}>
      {codes.length ? (
        codes.map(renderRow)
      ) : (
        <p className={styles.emptyState}>{emptyLabel}</p>
      )}
    </div>
  </div>
);

/**
 * Moves organizations between Included and Not included. Changes stay local
 * until "Save"; closing or cancelling discards them.
 */
const EditIncludedOrgsModal = ({
  open,
  includedCodes,
  organizations,
  organizationsLoadFailed,
  handleClose,
  handleSave,
}: IEditIncludedOrgsModalProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string[]>(includedCodes);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Every opening starts from the saved list.
  useEffect(() => {
    if (!open) return;
    setDraft(includedCodes);
    setSearch("");
    setSaveFailed(false);
  }, [open, includedCodes]);

  const logoByCode = useMemo(
    () => new Map(organizations.map((org) => [org.code, org.logoUrl] as const)),
    [organizations]
  );

  // Saved codes that no longer match an organization still show, so they
  // can be removed.
  const allCodes = useMemo(
    () =>
      Array.from(
        new Set([...includedCodes, ...organizations.map((org) => org.code)])
      ),
    [includedCodes, organizations]
  );

  const added = draft.filter((code) => !includedCodes.includes(code));
  const removed = includedCodes.filter((code) => !draft.includes(code));
  const hasChanges = added.length > 0 || removed.length > 0;

  const query = search.trim().toLowerCase();
  const matches = (code: string) => code.toLowerCase().includes(query);
  const includedRows = draft.filter(matches);
  const notIncludedRows = allCodes.filter(
    (code) => !draft.includes(code) && matches(code)
  );

  const emptyLabel = (isIncluded: boolean) => {
    if (query) return t("lbl_auto_cashout_settings_no_results");
    if (!isIncluded && organizationsLoadFailed)
      return t("lbl_auto_cashout_settings_orgs_load_failed");
    return t("lbl_auto_cashout_settings_column_empty");
  };

  const onClose = () => {
    if (!saving) handleClose();
  };

  const onSave = async () => {
    setSaving(true);
    setSaveFailed(false);
    try {
      await handleSave(added, removed);
    } catch (error) {
      console.error(error);
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (code: string, isIncluded: boolean) => {
    const isNew = isIncluded && added.includes(code);
    const isRemoved = !isIncluded && removed.includes(code);
    return (
      <div key={code} className={styles.row}>
        <OrgAvatar code={code} logoUrl={logoByCode.get(code)} />
        <span className={styles.code}>{code}</span>
        {isNew && (
          <span className={classNames(styles.tag, styles.tagNew)}>
            {t("lbl_auto_cashout_settings_tag_new")}
          </span>
        )}
        {isRemoved && (
          <span className={classNames(styles.tag, styles.tagRemoved)}>
            {t("lbl_auto_cashout_settings_tag_removed")}
          </span>
        )}
        <button
          type="button"
          disabled={saving}
          className={classNames(styles.rowAction, {
            [styles.rowActionRemove]: isIncluded,
          })}
          onClick={() =>
            setDraft((current) =>
              isIncluded
                ? current.filter((item) => item !== code)
                : [...current, code]
            )
          }>
          {isIncluded
            ? t("lbl_auto_cashout_settings_remove")
            : t("lbl_auto_cashout_settings_add")}
        </button>
      </div>
    );
  };

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      customModalClassName={styles.modal}>
      <div className={styles.container}>
        <div>
          <h1 className={styles.title}>
            {t("lbl_auto_cashout_settings_edit_title")}
          </h1>
          <p className={styles.subtitle}>
            {t("lbl_auto_cashout_settings_edit_subtitle")}
          </p>
        </div>

        <label className={styles.search}>
          <SearchIcon fontSize="small" className={styles.searchIcon} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("lbl_auto_cashout_settings_search_placeholder")}
          />
        </label>

        <div className={styles.columns}>
          <Column
            title={t("lbl_auto_cashout_settings_included_title")}
            codes={includedRows}
            emptyLabel={emptyLabel(true)}
            renderRow={(code) => renderRow(code, true)}
          />
          <Column
            title={t("lbl_auto_cashout_settings_not_included_title")}
            codes={notIncludedRows}
            emptyLabel={emptyLabel(false)}
            renderRow={(code) => renderRow(code, false)}
          />
        </div>

        {saveFailed && (
          <p className={styles.error}>
            {t("lbl_auto_cashout_settings_save_failed")}
          </p>
        )}

        <div className={styles.actions}>
          <PrimaryButton disabled={!hasChanges || saving} onClick={onSave}>
            {saving ? <Spinner /> : t("lbl_auto_cashout_settings_save")}
          </PrimaryButton>
          <button
            type="button"
            className={styles.cancelButton}
            disabled={saving}
            onClick={onClose}>
            {t("lbl_cancel")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditIncludedOrgsModal;
